import type { Book, ReadingProgressEntry, ReadingSession } from '../domain/entities/types';
import { normalizeBibleChapterIds } from '../domain/bibleCanon';
import {
  SYNC_PAYLOAD_VERSION_V1,
  SYNC_PAYLOAD_VERSION_V2,
  canonicalizationForPayload,
  type Sha256Hex,
  type SyncChange,
  type SyncChangeIntegrityVerification,
  type SyncEntityType,
  type SyncPayloadMap,
  type SyncPayloadVersion
} from './types';
import {
  bookSchema,
  noteSchema,
  quoteSchema,
  readingProgressEntrySchema,
  readingSessionSchema
} from '../domain/schemas/schemas';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const UUID_KEYS = new Set([
  'id', 'bookId', 'seriesId', 'sessionId', 'supersededByEntryId', 'correctionOfEntryIds'
]);
const INSTANT_KEYS = new Set([
  'createdAt', 'updatedAt', 'startedAt', 'finishedAt', 'pausedAt', 'recordedAt', 'changedAt'
]);
const LOCAL_DATE_KEYS = new Set(['startedOn', 'finishedOn', 'occurredOn', 'changedOn', 'date']);
const SET_ARRAY_KEYS = new Set(['genres', 'correctionOfEntryIds']);
const BIBLE_CHAPTER_ARRAY_KEYS = new Set(['completedChapterIds', 'chapterIds']);

const compareBinary = (left: string, right: string): number => left < right ? -1 : left > right ? 1 : 0;

const normalizeInstant = (value: string): string => {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) throw new Error(`Timestamp de sincronização inválido: ${value}`);
  return new Date(timestamp).toISOString();
};

const normalizeLocalDate = (value: string): string => {
  if (!LOCAL_DATE_PATTERN.test(value)) throw new Error(`Data local de sincronização inválida: ${value}`);
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) {
    throw new Error(`Data local de sincronização inválida: ${value}`);
  }
  return value;
};

const normalizeString = (key: string | undefined, value: string): string => {
  if (key && UUID_KEYS.has(key) && UUID_PATTERN.test(value)) return value.toLowerCase();
  if (key && INSTANT_KEYS.has(key)) return normalizeInstant(value);
  if (key && LOCAL_DATE_KEYS.has(key)) return normalizeLocalDate(value);
  return value;
};

const normalizeValue = (value: unknown, key?: string): unknown => {
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'string') return normalizeString(key, value);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('O payload de sincronização contém número não finito.');
    return Object.is(value, -0) ? 0 : value;
  }
  if (Array.isArray(value)) {
    const normalized = value.map((item) => normalizeValue(item, key));
    if (key && BIBLE_CHAPTER_ARRAY_KEYS.has(key)) {
      return normalizeBibleChapterIds(normalized.map(String));
    }
    if (!key || !SET_ARRAY_KEYS.has(key)) return normalized;
    const unique = new Map(normalized.map((item) => [canonicalStringify(item), item]));
    return [...unique.entries()].sort(([left], [right]) => compareBinary(left, right)).map(([, item]) => item);
  }
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const childKey of Object.keys(value as object).sort(compareBinary)) {
      const child = (value as Record<string, unknown>)[childKey];
      if (child !== undefined) result[childKey] = normalizeValue(child, childKey);
    }
    return result;
  }
  throw new Error('O payload de sincronização contém valor não serializável.');
};

export const canonicalStringify = (value: unknown): string => {
  const normalized = normalizeValue(value);
  if (normalized === null || typeof normalized === 'boolean' || typeof normalized === 'string') {
    return JSON.stringify(normalized);
  }
  if (typeof normalized === 'number') return JSON.stringify(normalized);
  if (Array.isArray(normalized)) return `[${normalized.map(canonicalStringify).join(',')}]`;
  const record = normalized as Record<string, unknown>;
  return `{${Object.keys(record).sort(compareBinary).map((childKey) =>
    `${JSON.stringify(childKey)}:${canonicalStringify(record[childKey])}`).join(',')}}`;
};

const bytesToHex = (value: ArrayBuffer): string =>
  [...new Uint8Array(value)].map((byte) => byte.toString(16).padStart(2, '0')).join('');

export const sha256Hex = async (value: string): Promise<Sha256Hex> =>
  bytesToHex(await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)));

export const projectSyncPayload = <E extends SyncEntityType>(
  entityType: E,
  payload: SyncPayloadMap[E]
): SyncPayloadMap[E] => {
  if (entityType === 'book') {
    const syncBook: Partial<Book> = { ...(payload as Book) };
    delete syncBook.coverAssetId;
    return normalizeValue(syncBook) as SyncPayloadMap[E];
  }
  if (entityType === 'reading_session' && (payload as ReadingSession).status !== 'completed') {
    throw new Error('Somente sessões concluídas podem integrar um batch de sincronização.');
  }
  return normalizeValue(payload) as SyncPayloadMap[E];
};

export const payloadVersionForSyncPayload = (
  entityType: SyncEntityType,
  payload: unknown
): SyncPayloadVersion => {
  const value = payload as Record<string, unknown> | undefined;
  const progress = value?.progress as Record<string, unknown> | undefined;
  const startProgress = value?.startProgress as Record<string, unknown> | undefined;
  const endProgress = value?.endProgress as Record<string, unknown> | undefined;
  const locator = value?.locator as Record<string, unknown> | undefined;
  const epub = entityType === 'book'
    ? progress?.kind === 'epub'
    : entityType === 'reading_session'
      ? startProgress?.kind === 'epub' || endProgress?.kind === 'epub'
      : entityType === 'reading_progress_entry'
        ? value?.kind === 'epub'
        : entityType === 'note' || entityType === 'quote'
          ? locator?.kind === 'epub_cfi'
          : false;
  return epub ? SYNC_PAYLOAD_VERSION_V2 : SYNC_PAYLOAD_VERSION_V1;
};

export const validateSyncPayloadForVersion = (
  entityType: SyncEntityType,
  payload: unknown,
  payloadVersion: SyncPayloadVersion
): void => {
  const expected = payloadVersionForSyncPayload(entityType, payload);
  if (expected !== payloadVersion) {
    throw new Error(`Payload ${entityType} exige payloadVersion ${expected}, mas recebeu ${payloadVersion}.`);
  }
  if (payloadVersion !== SYNC_PAYLOAD_VERSION_V2) return;
  if (entityType === 'book') bookSchema.parse(payload);
  else if (entityType === 'reading_session') {
    const session = readingSessionSchema.parse(payload);
    if (session.status !== 'completed') throw new Error('Somente sessões EPUB concluídas integram o Sync.');
  } else if (entityType === 'reading_progress_entry') readingProgressEntrySchema.parse(payload);
  else if (entityType === 'note') noteSchema.parse(payload);
  else if (entityType === 'quote') quoteSchema.parse(payload);
};

export const projectReadingProgressEntryForSync = (
  entry: ReadingProgressEntry,
  referencedSessionExists: boolean
): ReadingProgressEntry => {
  const projected = { ...entry };
  if (projected.supersededByEntryId && projected.sessionId && !referencedSessionExists) {
    delete projected.sessionId;
  }
  return projectSyncPayload('reading_progress_entry', projected);
};

export const hashSyncPayload = async <E extends SyncEntityType>(
  entityType: E,
  payload: SyncPayloadMap[E]
): Promise<Sha256Hex> => {
  if (payloadVersionForSyncPayload(entityType, payload) !== SYNC_PAYLOAD_VERSION_V1) {
    throw new Error('Payload EPUB exige canonicalização leitour-sync-json-v2.');
  }
  return hashSyncPayloadForVersion(entityType, payload, SYNC_PAYLOAD_VERSION_V1);
};

export const hashSyncPayloadForEntity = async <E extends SyncEntityType>(
  entityType: E,
  payload: SyncPayloadMap[E]
): Promise<Sha256Hex> => hashSyncPayloadForVersion(
  entityType, payload, payloadVersionForSyncPayload(entityType, payload)
);

export const hashSyncPayloadForVersion = async (
  entityType: SyncEntityType,
  payload: unknown,
  payloadVersion: SyncPayloadVersion
): Promise<Sha256Hex> => sha256Hex(canonicalStringify({
  canonicalization: canonicalizationForPayload(payloadVersion),
  entityType,
  payloadVersion,
  payload: projectSyncPayload(entityType, payload as SyncPayloadMap[typeof entityType])
}));

/**
 * Mandatory integrity gate for future snapshot/pull transport. A caller must
 * accept the change and advance its cursor only when this returns `verified`.
 */
export const verifyRemoteSyncChangeIntegrity = async <E extends SyncEntityType>(
  change: SyncChange<E>
): Promise<SyncChangeIntegrityVerification<E>> => {
  const calculatedPayloadHash = await hashSyncPayloadForVersion(
    change.entityType, change.payload, change.payloadVersion
  );
  if (calculatedPayloadHash === change.payloadHash) {
    return { status: 'verified', change, calculatedPayloadHash };
  }
  return {
    status: 'invalid',
    code: 'payload_hash_mismatch',
    retryable: false,
    declaredPayloadHash: change.payloadHash,
    calculatedPayloadHash,
    mustRejectChange: true,
    mustRecordIntegrityError: true,
    mustPreserveCursor: true,
    mustPreserveLocalData: true
  };
};
